// LPC-14 formant extraction (FR-3). Levinson-Durbin recursion + polynomial root finding.

export interface Formants {
  f1: number;
  f2: number;
  f3: number;
}

// Autocorrelation up to lag `order`.
function autocorrelate(signal: Float32Array, order: number): Float32Array {
  const r = new Float32Array(order + 1);
  for (let k = 0; k <= order; k++) {
    let sum = 0;
    for (let i = 0; i + k < signal.length; i++) sum += signal[i] * signal[i + k];
    r[k] = sum;
  }
  return r;
}

// Levinson-Durbin: returns LPC coefficients a[1..order] (index 0 unused = 0).
function levinson(r: Float32Array, order: number): Float32Array {
  const a = new Float32Array(order + 1);
  const tmp = new Float32Array(order + 1);
  let e = r[0];
  if (e <= 0) return a;
  a[0] = 1;
  for (let i = 1; i <= order; i++) {
    let acc = r[i];
    for (let j = 1; j < i; j++) acc -= a[j] * r[i - j];
    const k = acc / e;
    const newE = e * (1 - k * k);
    for (let j = 1; j < i; j++) tmp[j] = a[j] - k * a[i - j];
    for (let j = 1; j < i; j++) a[j] = tmp[j];
    a[i] = k;
    e = newE;
    if (e <= 0) break;
  }
  return a;
}

interface Complex {
  re: number;
  im: number;
}

// Durand-Kerner root finding for polynomial with real coefficients.
// coeffs[0] is highest-degree term; polynomial = sum coeffs[i]*x^(n-i).
function findRoots(coeffs: number[]): Complex[] {
  const n = coeffs.length - 1;
  // Normalize by leading coefficient.
  const a = coeffs.map((c) => c / coeffs[0]);
  // Remove leading zeros / trailing near-zero roots handled implicitly.
  const roots: Complex[] = [];
  const seed = 0.4;
  for (let i = 0; i < n; i++) {
    const angle = (2 * Math.PI * i) / n + seed;
    roots.push({ re: Math.cos(angle), im: Math.sin(angle) });
  }
  const evalP = (z: Complex): Complex => {
    let re = a[0];
    let im = 0;
    for (let i = 1; i <= n; i++) {
      const nre = re * z.re - im * z.im + a[i];
      im = re * z.im + im * z.re;
      re = nre;
    }
    return { re, im };
  };
  for (let iter = 0; iter < 100; iter++) {
    let maxDelta = 0;
    for (let i = 0; i < n; i++) {
      const num = evalP(roots[i]);
      let denRe = 1;
      let denIm = 0;
      for (let j = 0; j < n; j++) {
        if (j === i) continue;
        const dre = roots[i].re - roots[j].re;
        const dim = roots[i].im - roots[j].im;
        const nre = denRe * dre - denIm * dim;
        denIm = denRe * dim + denIm * dre;
        denRe = nre;
      }
      const denom = denRe * denRe + denIm * denIm;
      if (denom === 0) continue;
      const deltaRe = (num.re * denRe + num.im * denIm) / denom;
      const deltaIm = (num.im * denRe - num.re * denIm) / denom;
      roots[i].re -= deltaRe;
      roots[i].im -= deltaIm;
      const mag = Math.sqrt(deltaRe * deltaRe + deltaIm * deltaIm);
      if (mag > maxDelta) maxDelta = mag;
    }
    if (maxDelta < 1e-12) break;
  }
  return roots;
}

// Extract F1/F2/F3 from an LPC analysis of the signal.
export function extractFormants(signal: Float32Array, sampleRate: number, order = 14): Formants {
  const r = autocorrelate(signal, order);
  const a = levinson(r, order);
  // Polynomial: 1 + a[1] z^-1 + ... + a[order] z^-order.
  // For root finding, coeffs highest-first: [1, a[1], ..., a[order]].
  const coeffs = [1];
  for (let i = 1; i <= order; i++) coeffs.push(a[i]);
  const roots = findRoots(coeffs);

  const candidates: { freq: number; bw: number }[] = [];
  for (const root of roots) {
    // Only roots in upper half plane (im > 0) correspond to resonances.
    if (root.im <= 0) continue;
    const freq = (Math.atan2(root.im, root.re) / (2 * Math.PI)) * sampleRate;
    const bw = (-(Math.log(Math.sqrt(root.re * root.re + root.im * root.im))) / Math.PI) * sampleRate;
    // Valid formant: positive freq, reasonable bandwidth (< 400 Hz), in voice range.
    if (freq > 90 && freq < 6000 && bw < 700) candidates.push({ freq, bw });
  }
  candidates.sort((x, y) => x.freq - y.freq);

  const f1 = candidates[0]?.freq ?? 0;
  const f2 = candidates[1]?.freq ?? 0;
  const f3 = candidates[2]?.freq ?? 0;
  return { f1, f2, f3 };
}
